import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout from "@/components/Layout";
import { useAuth, API } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Trash2, Copy, Users, Building2, Key, Bell, BellOff, Send, ExternalLink, Unlink, CalendarDays, Wallet, Pencil, Sparkles, Bot, Blocks } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConexaoIA from "@/components/ConexaoIA";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { formatDateBR } from "@/lib/dates";
import { isOwner as hasOwnerRole } from "@/lib/permissions";
import { LEITURA } from "../lib/larguras";

// A tela tinha oito cartões empilhados: quem vinha trocar a OAB rolava por
// notificações, IA e cobrança até achar. As abas separam por assunto e o
// endereço guarda qual está aberta, para o link compartilhado abrir no lugar.
export const ABAS = [
  { chave: "escritorio", rotulo: "Escritório", icone: Building2 },
  { chave: "notificacoes", rotulo: "Notificações", icone: Bell },
  { chave: "assistente", rotulo: "Assistente de IA", icone: Bot },
  { chave: "integracoes", rotulo: "Integrações", icone: Blocks },
];

export function abaDaUrl(busca, hash = "") {
  const params = new URLSearchParams(busca || "");
  const pedida = params.get("aba");
  if (ABAS.some((a) => a.chave === pedida)) return pedida;
  // Os avisos antigos apontam para a seção, não para a aba.
  if (params.get("ativar") === "chat" || hash === "#chat") return "assistente";
  if (params.get("ativar") === "telegram") return "notificacoes";
  if (params.get("gcal") || params.get("asaas")) return "integracoes";
  return "escritorio";
}

function AbasConfiguracoes({ aba, onTrocar }) {
  return (
    <div className="border-b border-[#D1DEEE] flex gap-1 overflow-x-auto" role="tablist" data-testid="abas-configuracoes">
      {ABAS.map(({ chave, rotulo, icone: Icone }) => {
        const ativa = chave === aba;
        return (
          <button
            key={chave}
            type="button"
            role="tab"
            aria-selected={ativa}
            data-testid={`aba-${chave}`}
            onClick={() => onTrocar(chave)}
            className={`flex items-center gap-2 px-4 py-3 font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              ativa
                ? "border-[#003882] text-[#003882]"
                : "border-transparent text-[#335480] hover:text-[#003882]"
            }`}
          >
            <Icone size={16} strokeWidth={1.5} aria-hidden="true" />
            {rotulo}
          </button>
        );
      })}
    </div>
  );
}

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

export default function ConfiguracoesPage() {
  const { user } = useAuth();
  const isOwner = hasOwnerRole(user);
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [oabs, setOabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workspaceName, setWorkspaceName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [leituraIA, setLeituraIA] = useState(false);
  const [aba, setAba] = useState(() => abaDaUrl(window.location.search, window.location.hash));
  const [showAddOab, setShowAddOab] = useState(false);
  const [oabNumero, setOabNumero] = useState("");
  const [oabUf, setOabUf] = useState("SP");
  const [oabTitular, setOabTitular] = useState("");
  const [addingOab, setAddingOab] = useState(false);
  const [deleteOabId, setDeleteOabId] = useState(null);
  // Corrigir um dígito exigia excluir e cadastrar de novo — o que consome o
  // limite do plano no meio do caminho.
  const [editandoOab, setEditandoOab] = useState(null);
  const [salvandoOab, setSalvandoOab] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [syncingOab, setSyncingOab] = useState({});
  const [telegram, setTelegram] = useState({ configured: false, linked: false });
  // O convite no e-mail de intimação aponta para cá. Sem levar direto à seção,
  // a pessoa cai no topo de uma página com sete blocos e a intenção se perde
  // exatamente onde ela era maior.
  const secaoTelegram = useRef(null);
  const [destacarTelegram, setDestacarTelegram] = useState(false);
  const [telegramLink, setTelegramLink] = useState(null); // { code, deep_link }
  const [connectingTelegram, setConnectingTelegram] = useState(false);
  const [unlinkingTelegram, setUnlinkingTelegram] = useState(false);
  const [asaas, setAsaas] = useState(null);
  const [asaasKey, setAsaasKey] = useState("");
  const [savingAsaas, setSavingAsaas] = useState(false);
  const [trocandoChave, setTrocandoChave] = useState(false);
  const [webhook, setWebhook] = useState(null);
  const [gcal, setGcal] = useState({ configured: false, linked: false, google_email: null });
  const [connectingGcal, setConnectingGcal] = useState(false);
  const [unlinkingGcal, setUnlinkingGcal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const wsRes = await axios.get(`${API}/auth/workspace`, { withCredentials: true });
      setWorkspace(wsRes.data.workspace);
      setMembers(Array.isArray(wsRes.data.members) ? wsRes.data.members : []);
      setWorkspaceName(wsRes.data.workspace?.name || "");
      setNotificationsEnabled(wsRes.data.workspace?.notifications_enabled !== false);
      setLeituraIA(Boolean(wsRes.data.workspace?.ia_agenda_automatica));
      if (isOwner) {
        try {
          const oabsRes = await axios.get(`${API}/oabs`, { withCredentials: true });
          setOabs(Array.isArray(oabsRes.data) ? oabsRes.data : []);
        } catch {
          toast.error("Erro ao carregar OABs");
        }
      }
    } catch {
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  }, [isOwner]);

  const loadTelegramStatus = async () => {
    try {
      const res = await axios.get(`${API}/telegram/status`, { withCredentials: true });
      setTelegram(res.data);
      if (res.data.linked) setTelegramLink(null);
    } catch {}
  };

  useEffect(() => { if (isOwner) loadTelegramStatus(); }, [isOwner]);

  // ?ativar=telegram vem do convite no e-mail de intimação.
  useEffect(() => {
    if (!telegram.configured || telegram.linked) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("ativar") !== "telegram") return;
    const alvo = secaoTelegram.current;
    if (!alvo) return;
    alvo.scrollIntoView({ behavior: "smooth", block: "center" });
    setDestacarTelegram(true);
    const timer = setTimeout(() => setDestacarTelegram(false), 3000);
    return () => clearTimeout(timer);
  }, [telegram.configured, telegram.linked]);

  // Enquanto há um código pendente, verifica a cada 3s se o vínculo foi concluído
  useEffect(() => {
    if (!telegramLink) return;
    const t = setInterval(loadTelegramStatus, 3000);
    return () => clearInterval(t);
  }, [telegramLink]);

  const connectTelegram = async () => {
    setConnectingTelegram(true);
    try {
      const res = await axios.post(`${API}/telegram/link`, {}, { withCredentials: true });
      setTelegramLink(res.data);
      window.open(res.data.deep_link, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar código do Telegram");
    } finally {
      setConnectingTelegram(false);
    }
  };

  const unlinkTelegram = async () => {
    setUnlinkingTelegram(true);
    try {
      await axios.delete(`${API}/telegram/link`, { withCredentials: true });
      setTelegram((t) => ({ ...t, linked: false }));
      setTelegramLink(null);
      toast.success("Telegram desvinculado");
    } catch {
      toast.error("Erro ao desvincular");
    } finally {
      setUnlinkingTelegram(false);
    }
  };

  const loadGcalStatus = async () => {
    try {
      const res = await axios.get(`${API}/gcal/status`, { withCredentials: true });
      setGcal(res.data);
    } catch {}
  };

  useEffect(() => { loadGcalStatus(); }, []);

  // Feedback do retorno do OAuth (?gcal=connected|sem_permissao|error)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("gcal");
    if (!result) return;
    if (result === "connected") toast.success("Google Agenda conectado — seus prazos serão sincronizados");
    // A tela do Google traz a permissão da agenda como caixa à parte; sem ela marcada não há o que sincronizar.
    else if (result === "sem_permissao") toast.error(
      "A permissão da agenda ficou desmarcada na tela do Google. Conecte de novo e marque a opção de criar agendas.",
      { duration: 10000 },
    );
    else toast.error("Não foi possível conectar o Google Agenda. Tente novamente.");
    params.delete("gcal");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }, []);

  const connectGcal = async () => {
    setConnectingGcal(true);
    try {
      const res = await axios.get(`${API}/gcal/connect`, { withCredentials: true });
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao iniciar conexão com o Google Agenda");
      setConnectingGcal(false);
    }
  };

  const unlinkGcal = async () => {
    setUnlinkingGcal(true);
    try {
      await axios.delete(`${API}/gcal/link`, { withCredentials: true });
      setGcal((g) => ({ ...g, linked: false, google_email: null }));
      toast.success("Google Agenda desconectado");
    } catch {
      toast.error("Erro ao desconectar");
    } finally {
      setUnlinkingGcal(false);
    }
  };

  // 403/503 aqui é esperado: financeiro é PRO e o card simplesmente não aparece.
  const loadAsaas = async () => {
    try {
      const res = await axios.get(`${API}/asaas/status`, { withCredentials: true });
      setAsaas(res.data);
    } catch { setAsaas(null); }
  };

  useEffect(() => { loadAsaas(); }, []);

  const loadWebhook = async () => {
    try {
      const res = await axios.get(`${API}/asaas/webhook-config`, { withCredentials: true });
      setWebhook(res.data);
    } catch { setWebhook(null); }
  };

  useEffect(() => { if (asaas?.connected) loadWebhook(); }, [asaas?.connected]);

  const gerarTokenWebhook = async () => {
    try {
      const res = await axios.post(`${API}/asaas/webhook-config`, {}, { withCredentials: true });
      setWebhook(res.data);
      toast.success("Token gerado — cadastre no painel do Asaas");
    } catch {
      toast.error("Não foi possível gerar o token");
    }
  };

  const connectAsaas = async (e) => {
    e.preventDefault();
    setSavingAsaas(true);
    try {
      await axios.post(`${API}/asaas/connect`, { api_key: asaasKey.trim() }, { withCredentials: true });
      setAsaasKey("");
      setTrocandoChave(false);
      await loadAsaas();
      toast.success("Asaas conectado");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Não foi possível conectar o Asaas");
    } finally {
      setSavingAsaas(false);
    }
  };

  const disconnectAsaas = async () => {
    setSavingAsaas(true);
    try {
      await axios.delete(`${API}/asaas/connect`, { withCredentials: true });
      await loadAsaas();
      toast.success("Asaas desconectado");
    } catch {
      toast.error("Erro ao desconectar");
    } finally {
      setSavingAsaas(false);
    }
  };

  const getInviteCode = async () => {
    try {
      const res = await axios.get(`${API}/auth/invite-code`, { withCredentials: true });
      setInviteCode(res.data.invite_code);
    } catch {}
  };

  useEffect(() => {
    load();
    if (isOwner) getInviteCode();
  }, [load, isOwner]);

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!workspaceName.trim()) return;
    setSavingName(true);
    try {
      await axios.put(`${API}/auth/workspace`, { name: workspaceName }, { withCredentials: true });
      setWorkspace((w) => ({ ...w, name: workspaceName }));
      toast.success("Nome atualizado!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSavingName(false);
    }
  };

  const handleAddOab = async (e) => {
    e.preventDefault();
    setAddingOab(true);
    try {
      const res = await axios.post(`${API}/oabs`, {
        numero: oabNumero,
        uf: oabUf,
        nome_titular: oabTitular || null,
      }, { withCredentials: true });
      setOabs((prev) => [...prev, res.data]);
      setShowAddOab(false);
      setOabNumero("");
      setOabTitular("");
      toast.success("OAB cadastrada! Sincronizando intimações…");
      // Sync inicial roda em background no backend — recarrega após alguns segundos
      setTimeout(() => { load(); toast.info("Intimações sincronizadas. Confira o painel."); }, 8000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar OAB");
    } finally {
      setAddingOab(false);
    }
  };

  const handleDeleteOab = async () => {
    if (!deleteOabId) return;
    try {
      await axios.delete(`${API}/oabs/${deleteOabId}`, { withCredentials: true });
      setOabs((prev) => prev.filter((o) => o.oab_id !== deleteOabId));
      toast.success("OAB removida");
    } catch {
      toast.error("Erro ao remover OAB");
    } finally {
      setDeleteOabId(null);
    }
  };

  const salvarOab = async () => {
    if (!editandoOab) return;
    setSalvandoOab(true);
    try {
      const { data } = await axios.patch(
        `${API}/oabs/${editandoOab.oab_id}`,
        {
          numero: editandoOab.numero,
          uf: editandoOab.uf,
          nome_titular: editandoOab.nome_titular || null,
        },
        { withCredentials: true },
      );
      setOabs((atual) => atual.map((o) => (o.oab_id === data.oab_id ? data : o)));
      setEditandoOab(null);
      if (data.intimacoes_do_numero_anterior > 0) {
        toast.info(
          `OAB atualizada. As ${data.intimacoes_do_numero_anterior} intimações já capturadas vieram do número anterior; ` +
          "estamos buscando as do número novo.",
        );
      } else {
        toast.success("OAB atualizada.");
      }
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || "Não foi possível atualizar a OAB.");
    } finally {
      setSalvandoOab(false);
    }
  };

  const handleSyncOab = async (oabId) => {
    setSyncingOab((s) => ({ ...s, [oabId]: true }));
    try {
      const res = await axios.post(`${API}/oabs/${oabId}/sync`, {}, { withCredentials: true });
      toast.success(res.data.synced_today ? "Já sincronizado hoje" : `${res.data.new_count} novas intimações`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao sincronizar");
    } finally {
      setSyncingOab((s) => ({ ...s, [oabId]: false }));
    }
  };

  const trocarAba = (nova) => {
    setAba(nova);
    const params = new URLSearchParams(window.location.search);
    params.set("aba", nova);
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  };

  const alternarLeituraIA = async () => {
    const novo = !leituraIA;
    try {
      await axios.put(`${API}/auth/workspace/ia-agenda`, { enabled: novo }, { withCredentials: true });
      setLeituraIA(novo);
      toast.success(
        novo
          ? "O assistente vai ler as intimações novas e lançar os prazos na agenda."
          : "Leitura automática desligada.",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível alterar a leitura automática.");
    }
  };

  const handleToggleNotifications = async () => {
    const newVal = !notificationsEnabled;
    try {
      await axios.put(`${API}/auth/workspace/notifications`, { enabled: newVal }, { withCredentials: true });
      setNotificationsEnabled(newVal);
      toast.success(newVal ? "Notificações ativadas" : "Notificações desativadas");
    } catch {
      toast.error("Erro ao atualizar notificações");
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/convite/${inviteCode}`;
    navigator.clipboard.writeText(link);
    toast.success("Link de convite copiado!");
  };

  const isPlanPro = workspace?.plan === "pro";

  return (
    <Layout>
      <div className={`${LEITURA} space-y-8`}>
        {/* Header */}
        <div className="animate-fade-in-1">
          <p className="text-[#335480] text-sm uppercase tracking-widest font-medium mb-1">Conta</p>
          <h1 className="text-4xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
            Configurações
          </h1>
        </div>

        <AbasConfiguracoes aba={aba} onTrocar={trocarAba} />

        <div className={aba === "notificacoes" ? "space-y-8" : "hidden"}>
        {/* Notificações */}
        <div className="bg-white border border-[#D1DEEE] rounded-lg p-6 animate-fade-in-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {notificationsEnabled ? (
                <Bell size={20} className="text-[#003882]" />
              ) : (
                <BellOff size={20} className="text-[#335480]" />
              )}
              <div>
                <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
                  Notificações por Email
                </h2>
                <p className="text-[#335480] text-sm mt-0.5">
                  Receba alertas de novas intimações e prazos por email
                </p>
              </div>
            </div>
            <button
              data-testid="toggle-notifications-btn"
              onClick={handleToggleNotifications}
              disabled={!isOwner}
              aria-label={isOwner ? "Alternar notificações por email" : "Somente o proprietário pode alterar as notificações"}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                notificationsEnabled ? "bg-[#003882]" : "bg-[#D1DEEE]"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transform transition-transform ${
                  notificationsEnabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
          {notificationsEnabled && (
            <div className="mt-4 p-3 bg-[#e5edf5] rounded-lg">
              <p className="text-[#003882] text-sm font-medium">Alertas ativos:</p>
              <ul className="text-[#335480] text-sm mt-1 space-y-1">
                <li>• Novas intimações do DJEN (monitorado a cada 30 minutos)</li>
                <li>• Lembrete 1, 3, 7 e 15 dias antes de audiências e prazos</li>
              </ul>
              <p className="text-[#335480] text-xs mt-2">Enviados para: todos os usuários do escritório</p>
            </div>
          )}
        </div>

        {/* Telegram */}
        {isOwner && telegram.configured && (
          <div
            id="telegram"
            ref={secaoTelegram}
            className={`bg-white border rounded-lg p-6 animate-fade-in-2 transition-colors ${
              destacarTelegram ? "border-[#003882] bg-[#F4F7FB]" : "border-[#D1DEEE]"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Send size={20} className={telegram.linked ? "text-[#003882]" : "text-[#335480]"} />
                <div>
                  <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
                    Notificações via Telegram
                  </h2>
                  <p className="text-[#335480] text-sm mt-0.5">
                    Receba os mesmos alertas de intimações e prazos direto no Telegram
                  </p>
                </div>
              </div>
              {telegram.linked ? (
                <Button
                  variant="outline"
                  onClick={unlinkTelegram}
                  disabled={unlinkingTelegram}
                  data-testid="telegram-unlink-btn"
                  className="border-[#D1DEEE] text-[#335480] hover:border-[#003882] hover:text-[#003882]"
                >
                  <Unlink size={15} /> Desconectar
                </Button>
              ) : (
                <Button
                  onClick={connectTelegram}
                  disabled={connectingTelegram}
                  data-testid="telegram-connect-btn"
                  className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
                >
                  <Send size={15} /> Conectar Telegram
                </Button>
              )}
            </div>

            {telegram.linked && (
              <div className="mt-4 p-3 bg-[#e5edf5] rounded-lg flex items-center gap-2 text-[#003882] text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-[#003882] flex-shrink-0" />
                Conectado — os alertas do escritório chegam no seu chat do Telegram
              </div>
            )}

            {!telegram.linked && telegramLink && (
              <div className="mt-4 p-4 bg-[#F4F7FB] border border-[#D1DEEE] rounded-lg">
                <p className="text-[#001533] text-sm font-medium mb-2">
                  Abrimos o Telegram em outra aba. Se não abriu, clique no link abaixo e toque em "Iniciar":
                </p>
                <a
                  href={telegramLink.deep_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[#003882] font-medium text-sm hover:underline"
                >
                  {telegramLink.deep_link} <ExternalLink size={13} />
                </a>
                <p className="text-[#335480] text-xs mt-2">
                  Aguardando confirmação — o código expira em {telegramLink.expires_in_minutes} minutos.
                </p>
              </div>
            )}
          </div>
        )}

        </div>

        <div className={aba === "assistente" ? "space-y-8" : "hidden"}>
        <ConexaoIA isOwner={isOwner} />

        {isOwner && (
          <div className="bg-white border border-[#D1DEEE] rounded-lg p-6 animate-fade-in-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <Sparkles size={20} strokeWidth={1.5} className={leituraIA ? "text-[#003882]" : "text-[#335480]"} />
                <div>
                  <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
                    Leitura automática das intimações
                  </h2>
                  <p className="text-[#335480] text-sm mt-0.5">
                    O modelo lê cada intimação nova, identifica o tipo do ato e joga o prazo na agenda.
                  </p>
                </div>
              </div>
              <button
                data-testid="toggle-leitura-ia"
                onClick={alternarLeituraIA}
                aria-label="Alternar leitura automática das intimações"
                className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${
                  leituraIA ? "bg-[#003882]" : "bg-[#D1DEEE]"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transform transition-transform ${
                    leituraIA ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            {leituraIA && (
              <div className="mt-4 p-3 bg-[#e5edf5] rounded-lg">
                <p className="text-[#335480] text-sm">
                  O compromisso nasce marcado como estimativa da IA — confira o prazo no processo antes
                  de contar com ele. O consumo é cobrado na sua conta do provedor.
                </p>
              </div>
            )}
            <div className="mt-4">
              <Link to="/uso-ia" className="text-[#003882] font-medium hover:underline" data-testid="link-extrato-ia">
                Ver extrato de uso do assistente
              </Link>
            </div>
          </div>
        )}



        </div>

        <div className={aba === "integracoes" ? "space-y-8" : "hidden"}>
        {/* Google Agenda */}
        {gcal.configured && (
          <div className="bg-white border border-[#D1DEEE] rounded-lg p-6 animate-fade-in-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CalendarDays size={20} className={gcal.linked ? "text-[#003882]" : "text-[#335480]"} />
                <div>
                  <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
                    Google Agenda
                  </h2>
                  <p className="text-[#335480] text-sm mt-0.5">
                    Prazos e audiências do escritório sincronizados no seu Google Agenda
                  </p>
                </div>
              </div>
              {gcal.linked ? (
                <Button
                  variant="outline"
                  onClick={unlinkGcal}
                  disabled={unlinkingGcal}
                  data-testid="gcal-unlink-btn"
                  className="border-[#D1DEEE] text-[#335480] hover:border-[#003882] hover:text-[#003882]"
                >
                  <Unlink size={15} /> Desconectar
                </Button>
              ) : (
                <Button
                  onClick={connectGcal}
                  disabled={connectingGcal}
                  data-testid="gcal-connect-btn"
                  className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
                >
                  <CalendarDays size={15} /> Conectar Google Agenda
                </Button>
              )}
            </div>

            {gcal.linked && (
              <div className="mt-4 p-3 bg-[#e5edf5] rounded-lg flex items-center gap-2 text-[#003882] text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-[#003882] flex-shrink-0" />
                Conectado{gcal.google_email ? ` como ${gcal.google_email}` : ""} — o calendário
                "TUTO — Prazos e Audiências" foi criado na sua conta Google
              </div>
            )}

            {!gcal.linked && (
              <p className="text-[#335480] text-xs mt-3">
                O TUTO cria um calendário próprio na sua conta — sem acesso aos seus outros eventos do Google.
              </p>
            )}
          </div>
        )}

        {/* Asaas */}
        {asaas && (
          <div className="bg-white border border-[#D1DEEE] rounded-lg p-6 animate-fade-in-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Wallet size={20} className={asaas.connected ? "text-[#003882]" : "text-[#335480]"} />
                <div>
                  <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
                    Cobrança pelo Asaas
                  </h2>
                  <p className="text-[#335480] text-sm mt-0.5">
                    Conecte a conta do seu escritório para emitir boleto, Pix e cartão pelos lançamentos do financeiro
                  </p>
                </div>
              </div>
              {asaas.connected && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setTrocandoChave((v) => !v)}
                    data-testid="asaas-change-key-btn"
                    className="border-[#D1DEEE] text-[#335480] hover:border-[#003882] hover:text-[#003882]"
                  >
                    <Key size={15} /> {trocandoChave ? "Cancelar" : "Alterar chave"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={disconnectAsaas}
                    disabled={savingAsaas}
                    data-testid="asaas-disconnect-btn"
                    className="border-[#D1DEEE] text-[#335480] hover:border-[#003882] hover:text-[#003882]"
                  >
                    <Unlink size={15} /> Desconectar
                  </Button>
                </div>
              )}
            </div>

            {asaas.connected && (
              <div className="mt-4 p-3 bg-[#e5edf5] rounded-lg flex items-center gap-2 text-[#003882] text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-[#003882] flex-shrink-0" />
                Conectado{asaas.account_name ? ` — ${asaas.account_name}` : ""}
                {asaas.environment === "sandbox" && " · ambiente de teste (sandbox)"}
              </div>
            )}
            {asaas.connected && webhook && (
              <div className="mt-3 p-4 bg-[#F4F7FB] border border-[#D1DEEE] rounded-lg">
                <p className="text-[#001533] text-sm font-medium">Baixa automática das parcelas</p>
                <p className="text-[#335480] text-xs mt-1">
                  Cadastre este endereço em Integrações › Webhooks no painel do Asaas para que a
                  parcela seja baixada sozinha quando o cliente pagar.
                </p>
                <p className="text-[#001533] text-xs mt-2 font-mono break-all">{webhook.url}</p>
                {webhook.configured ? (
                  <details className="mt-2">
                    <summary className="text-[#003882] text-xs cursor-pointer">Ver token de autenticação</summary>
                    <p data-testid="asaas-webhook-token" className="text-[#001533] text-xs mt-1 font-mono break-all">{webhook.token}</p>
                    <Button variant="outline" onClick={gerarTokenWebhook} className="mt-2 h-8 text-xs border-[#D1DEEE] text-[#335480]">
                      Gerar novo token
                    </Button>
                  </details>
                ) : (
                  <Button onClick={gerarTokenWebhook} data-testid="asaas-webhook-token-btn" className="mt-3 h-9 bg-[#003882] hover:bg-[#00285e] text-white text-sm font-semibold">
                    Gerar token do webhook
                  </Button>
                )}
              </div>
            )}
            {(!asaas.connected || trocandoChave) && (
              <>
                {!asaas.connected && asaas.referral_url && (
                  <p className="text-[#335480] text-sm mt-4">
                    Ainda não tem conta?{" "}
                    <a
                      href={asaas.referral_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="asaas-referral-link"
                      className="text-[#003882] font-medium hover:underline inline-flex items-center gap-1"
                    >
                      Abra a sua no Asaas <ExternalLink size={13} />
                    </a>
                  </p>
                )}
                <form onSubmit={connectAsaas} className="mt-4 flex gap-3">
                  <Input
                    data-testid="asaas-key-input"
                    type="password"
                    value={asaasKey}
                    onChange={(e) => setAsaasKey(e.target.value)}
                    placeholder="Cole aqui a chave de API do Asaas"
                    className="flex-1 h-11 text-base border-[#D1DEEE]"
                  />
                  <Button
                    type="submit"
                    disabled={savingAsaas || asaasKey.trim().length < 20}
                    data-testid="asaas-connect-btn"
                    className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
                  >
                    {savingAsaas ? "Validando..." : "Conectar"}
                  </Button>
                </form>
                <p className="text-[#335480] text-xs mt-2">
                  A chave é validada no Asaas antes de ser guardada, e fica cifrada. Chave de
                  homologação é reconhecida sozinha e usa o ambiente de teste.
                </p>
              </>
            )}
          </div>
        )}

        </div>

        <div className={aba === "escritorio" ? "space-y-8" : "hidden"}>
        {/* Escritório */}
        <div className="bg-white border border-[#D1DEEE] rounded-lg p-6 animate-fade-in-2">
          <div className="flex items-center gap-3 mb-5">
            <Building2 size={20} className="text-[#003882]" />
            <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Escritório
            </h2>
          </div>
          <form onSubmit={handleSaveName} className="flex gap-3">
            <Input
              data-testid="workspace-name-input"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder="Nome do escritório"
              disabled={!isOwner}
              className="flex-1 h-11 text-base border-[#D1DEEE]"
            />
            {isOwner && (
              <Button
                type="submit"
                data-testid="save-workspace-name-btn"
                disabled={savingName}
                className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
              >
                {savingName ? "Salvando..." : "Salvar"}
              </Button>
            )}
          </form>
          <div className="mt-4 flex items-center gap-2">
            <span className={`text-xs px-3 py-1 rounded-full font-bold ${
              isPlanPro ? "bg-[#003882] text-white" : "bg-[#e5edf5] text-[#003882]"
            }`}>
              {isPlanPro ? "Plano PRO" : "Plano FREE"}
            </span>
          </div>
        </div>

        {/* OABs */}
        {isOwner && <div className="bg-white border border-[#D1DEEE] rounded-lg p-6 animate-fade-in-2">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <Key size={20} className="text-[#003882]" />
              <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
                OABs Cadastradas
              </h2>
              <span className="text-sm text-[#335480]">
                ({oabs.length}/{isPlanPro ? 10 : 1})
              </span>
            </div>
            {isOwner && <Button
              data-testid="add-oab-btn"
              onClick={() => setShowAddOab(true)}
              className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold h-9 px-4 text-sm"
            >
              <Plus size={16} />
              Adicionar OAB
            </Button>}
          </div>

          {oabs.length === 0 ? (
            <div className="py-8 text-center text-[#335480]">
              <Key size={32} className="mx-auto mb-3 opacity-30" />
              <p>Nenhuma OAB cadastrada. Cadastre para receber intimações do DJEN.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {oabs.map((oab, i) => (
                <div
                  key={oab.oab_id}
                  data-testid={`oab-row-${i}`}
                  className="flex items-center justify-between p-4 bg-[#F4F7FB] rounded-lg border border-[#D1DEEE]"
                >
                  <div>
                    <p className="text-[#001533] text-base font-bold">
                      OAB/{oab.uf} {oab.numero}
                    </p>
                    {oab.nome_titular && (
                      <p className="text-[#335480] text-sm">{oab.nome_titular}</p>
                    )}
                    {oab.last_sync_at && (
                      <p className="text-[#335480] text-xs mt-0.5">
                        Última sync: {formatDateBR(oab.last_sync_at)}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`sync-oab-btn-${i}`}
                      onClick={() => handleSyncOab(oab.oab_id)}
                      disabled={syncingOab[oab.oab_id]}
                      variant="outline"
                      className="border-[#D1DEEE] text-[#335480] hover:text-[#003882] text-sm h-8 px-3"
                    >
                      {syncingOab[oab.oab_id] ? "..." : "Sincronizar"}
                    </Button>
                    <button
                      data-testid={`edit-oab-btn-${i}`}
                      onClick={() => setEditandoOab({ ...oab })}
                      aria-label={`Editar OAB ${oab.uf} ${oab.numero}`}
                      className="p-2 rounded-lg text-[#335480] hover:text-[#003882] hover:bg-[#e5edf5] transition-colors"
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      data-testid={`delete-oab-btn-${i}`}
                      onClick={() => setDeleteOabId(oab.oab_id)}
                      className="p-2 rounded-lg text-[#335480] hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>}

        {/* Members */}
        <div className="bg-white border border-[#D1DEEE] rounded-lg p-6 animate-fade-in-3">
          <div className="flex items-center gap-3 mb-5">
            <Users size={20} className="text-[#003882]" />
            <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Usuários
            </h2>
            <span className="text-sm text-[#335480]">
              ({members.length}/{isPlanPro ? "∞" : 2})
            </span>
          </div>

          <div className="space-y-3 mb-5">
            {members.map((m, i) => (
              <div
                key={m.user_id}
                data-testid={`member-row-${i}`}
                className="flex items-center gap-3 p-3 bg-[#F4F7FB] rounded-lg"
              >
                <div className="w-9 h-9 rounded-full bg-[#e5edf5] flex items-center justify-center text-[#003882] font-bold text-sm">
                  {m.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div className="flex-1">
                  <p className="text-[#001533] font-semibold text-base">{m.name}</p>
                  <p className="text-[#335480] text-sm">{m.email}</p>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  m.role === "owner" ? "bg-[#003882] text-white" : "bg-[#e5edf5] text-[#003882]"
                }`}>
                  {m.role === "owner" ? "Proprietário" : "Membro"}
                </span>
              </div>
            ))}
          </div>

          {isOwner && (
            <div className="border-t border-[#D1DEEE] pt-4">
              <p className="text-[#001533] font-semibold text-base mb-2">Convidar usuário</p>
              <p className="text-[#335480] text-sm mb-3">
                Compartilhe o link abaixo para que outros advogados acessem este escritório.
              </p>
              <div className="flex gap-2">
                <Input
                  data-testid="invite-link-input"
                  value={inviteCode ? `${window.location.origin}/convite/${inviteCode}` : "Gerando..."}
                  readOnly
                  className="flex-1 h-10 text-sm border-[#D1DEEE] bg-[#F4F7FB] text-[#335480]"
                />
                <Button
                  data-testid="copy-invite-btn"
                  onClick={copyInviteLink}
                  variant="outline"
                  className="border-[#D1DEEE] text-[#335480] hover:text-[#003882] h-10"
                >
                  <Copy size={16} />
                  Copiar
                </Button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Add OAB Dialog */}
      <Dialog open={showAddOab} onOpenChange={setShowAddOab}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Cadastrar OAB
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddOab} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#001533] font-medium">Número *</Label>
                <Input
                  data-testid="oab-numero-input"
                  value={oabNumero}
                  onChange={(e) => setOabNumero(e.target.value)}
                  placeholder="123456"
                  required
                  className="mt-1.5 h-11 text-base border-[#D1DEEE]"
                />
              </div>
              <div>
                <Label className="text-[#001533] font-medium">UF *</Label>
                <select
                  data-testid="oab-uf-select"
                  value={oabUf}
                  onChange={(e) => setOabUf(e.target.value)}
                  className="mt-1.5 h-11 w-full border border-[#D1DEEE] rounded-lg px-3 text-base text-[#001533] focus:border-[#003882] focus:ring-2 focus:ring-[#003882]/20 bg-white"
                >
                  {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-[#001533] font-medium">Nome do titular (opcional)</Label>
              <Input
                data-testid="oab-titular-input"
                value={oabTitular}
                onChange={(e) => setOabTitular(e.target.value)}
                placeholder="Dr. João Silva"
                className="mt-1.5 h-11 text-base border-[#D1DEEE]"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <Button type="button" variant="outline" onClick={() => setShowAddOab(false)} className="flex-1 border-[#D1DEEE]">
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="confirm-add-oab-btn"
                disabled={addingOab}
                className="flex-1 bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
              >
                {addingOab ? "Cadastrando..." : "Cadastrar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete OAB Confirm */}
      <AlertDialog open={!!deleteOabId} onOpenChange={() => setDeleteOabId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#001533]">Encerrar o monitoramento desta OAB?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#335480]">
              O TUTO para de buscar intimações para ela e a vaga volta a ficar livre no seu plano.
              As intimações já capturadas continuam na sua lista, e você pode cadastrar a mesma
              OAB de novo depois.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-oab-btn"
              onClick={handleDeleteOab}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edição da OAB */}
      <Dialog open={Boolean(editandoOab)} onOpenChange={(aberto) => !aberto && setEditandoOab(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Cabinet Grotesk" }}>Editar OAB</DialogTitle>
          </DialogHeader>
          {editandoOab && (
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label htmlFor="oab-edit-numero" className="text-[#335480]">Número</Label>
                  <Input
                    id="oab-edit-numero"
                    data-testid="oab-edit-numero"
                    value={editandoOab.numero}
                    onChange={(e) => setEditandoOab((o) => ({ ...o, numero: e.target.value }))}
                    className="mt-1.5"
                  />
                </div>
                <div className="w-24">
                  <Label htmlFor="oab-edit-uf" className="text-[#335480]">UF</Label>
                  <Input
                    id="oab-edit-uf"
                    data-testid="oab-edit-uf"
                    value={editandoOab.uf}
                    maxLength={2}
                    onChange={(e) => setEditandoOab((o) => ({ ...o, uf: e.target.value.toUpperCase() }))}
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="oab-edit-titular" className="text-[#335480]">Titular (opcional)</Label>
                <Input
                  id="oab-edit-titular"
                  data-testid="oab-edit-titular"
                  value={editandoOab.nome_titular || ""}
                  onChange={(e) => setEditandoOab((o) => ({ ...o, nome_titular: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
              <p className="text-[#335480] text-sm">
                Mudar o número ou a UF faz o TUTO buscar as intimações do novo número. As já
                capturadas continuam na lista.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setEditandoOab(null)} className="border-[#D1DEEE] text-[#335480]">
                  Cancelar
                </Button>
                <Button
                  data-testid="oab-edit-salvar"
                  onClick={salvarOab}
                  disabled={salvandoOab || !editandoOab.numero?.trim() || editandoOab.uf?.length !== 2}
                  className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
                >
                  {salvandoOab ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
